import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { usePushSubscription, useGetPlayerTags } from 'server';
import { useUserSelector, useAppSelector } from 'js/store/hooks';
import { useNavigate } from 'js/hooks/navigate';
import { userSetAttributesAction, userUnsetAttributesAction } from 'js/hooks/user/store/actions';

const VERIFIED_TAG_NAME = 'uam/verification/conversion_verified';
const KYC_1_TAG_NAME = 'risk/kyc_x1';
const KYC_2_TAG_NAME = 'risk/kyc_x2';
const UPLOAD_REQUIREMENT_TAG_NAME = 'risk/doc/upload_requirement';
const tagList = [VERIFIED_TAG_NAME, KYC_1_TAG_NAME, KYC_2_TAG_NAME, UPLOAD_REQUIREMENT_TAG_NAME];

export const useUserVerificationStatus = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const getPlayerTags = useGetPlayerTags();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const { DTAG_DEPOSIT_PAGE = '/' } = dynamicTags;

    const handlePushMessage = useCallback(pushMessage => {
        if (pushMessage.ID !== 31133) {
            return;
        }

        const { notification: { notificationPlayerTagChangeInfo } = {} } = pushMessage.data;

        if (!notificationPlayerTagChangeInfo) {
            return;
        }

        const { tag, value, removed } = notificationPlayerTagChangeInfo;

        if (!tagList.includes(tag)) {
            return;
        }

        switch (tag) {
            case VERIFIED_TAG_NAME:
                dispatch(userSetAttributesAction({ isUserVerified: !removed }));
                if (!removed) {
                    navigate(DTAG_DEPOSIT_PAGE, { showUserVerifiedPopup: true });
                }
                break;
            case KYC_1_TAG_NAME:
                dispatch(userSetAttributesAction({ isUserKycMadeOnce: !removed }));
                break;
            case KYC_2_TAG_NAME:
                dispatch(userSetAttributesAction({ isUserKycMadeTwice: !removed }));
                break;
            case UPLOAD_REQUIREMENT_TAG_NAME:
                removed
                    ? dispatch(userUnsetAttributesAction('documentUploadRequirements'))
                    : dispatch(userSetAttributesAction({ documentUploadRequirements: value }));
                break;
            default: break;
        }

    }, [dispatch, navigate, DTAG_DEPOSIT_PAGE]);

    usePushSubscription(31133, () => handlePushMessage);

    useEffect(() => {
        if (!isLoggedIn) {
            dispatch(userSetAttributesAction({
                userVerificationTagsFetched: false
            }));

            return;
        }

        (async function() {
            const response = await getPlayerTags({ tagName: tagList.join(',') });

            const tags = response?.tags || [];
            const isUserVerified = Boolean(tags.find(tag => tag.name.toLowerCase() === VERIFIED_TAG_NAME));
            const isUserKycMadeOnce = Boolean(tags.find(tag => tag.name.toLowerCase() === KYC_1_TAG_NAME));
            const isUserKycMadeTwice = Boolean(tags.find(tag => tag.name.toLowerCase() === KYC_2_TAG_NAME));
            const uploadRequirements = tags.find(tag => tag.name.toLowerCase() === UPLOAD_REQUIREMENT_TAG_NAME);

            dispatch(userSetAttributesAction({
                userVerificationTagsFetched: true,
                isUserVerified,
                isUserKycMadeOnce,
                isUserKycMadeTwice,
                documentUploadRequirements: uploadRequirements?.value
            }));
        })();
    }, [isLoggedIn, dispatch, getPlayerTags]);
};
